import { useEffect } from "react";
import { NavLink, Outlet } from "react-router-dom";
import "../Dashboard/Dashboard.css";
import "../Teacher/Teacher.css";
import "./Setting.css";
import Avatar from "react-avatar";
import { Images } from "../../../../config/images";
import { useTenantAuth } from "../../../../context/TeanantAuthProvider";
import { useGetOrganizationReference } from "../../../../hooks/organization/useGetOrganizationReference";
import { usePermission } from "../../../../hooks/tenant/usePermission";
/**
 * Settings tabs configuration
 */
const SETTINGS_TABS = [
  {
    id: "profile",
    label: "Admin Profile",
    permission: "settings_profile",
    path: {
      school: "/school-admin/settings/profile",
      organization: "/organization/settings/profile",
    },
    allowedTypes: ["sub", "organization", "self"],
  },
  {
    id: "session",
    label: "Session",
    permission: "settings_school_session",
    path: {
      school: "/school-admin/settings/session",
    },
    allowedTypes: ["sub", "self"],
  },
  {
    id: "members",
    label: "Members",
    permission: "settings_school_members",
    path: {
      school: "/school-admin/settings/members",
      organization: "/organization/settings/members",
    },
    allowedTypes: ["sub", "self"],
  },
  {
    id: "payments",
    label: "Payments",
    permission: "settings_school_payments",
    path: {
      school: "/school-admin/settings/payments",
      organization: "/organization/settings/payments",
    },
    allowedTypes: ["organization", "self"],
  },
  {
    id: "invoices",
    label: "Invoices",
    permission: "settings_school_invoices",
    path: {
      school: "/school-admin/settings/invoices",
      organization: "/organization/settings/invoices",
    },
    allowedTypes: ["organization", "self"],
  },
  {
    id: "licenses",
    label: "Licences",
    permission: "settings_school_licenses",
    path: {
      school: "/school-admin/settings/licenses",
      organization: "/organization/settings/licenses",
    },
    allowedTypes: ["sub", "organization", "self"],
  },
];

/**
 * SettingsLayout - Shared layout component for all settings pages
 * @param {Object} param
 * @param {"school"|"organization"} [param.type]
 */
const SettingsLayout = ({ type = "school" }) => {
  const { currentUser } = useTenantAuth();
  const { can } = usePermission();
  const { referenceData, loading, getOrganizationReference } =
    useGetOrganizationReference();

  // Fetch organization reference data on mount
  useEffect(() => {
    if (currentUser?.school_id) {
      getOrganizationReference({ institutionId: currentUser.school_id });
    }
  }, [currentUser?.school_id]);

  const filteredTabs = SETTINGS_TABS.filter(
    (tab) =>
      tab.allowedTypes.includes(currentUser?.auth?.tenant_type) &&
      can(tab.permission),
  );

  // Determine admin title based on type or API response
  const getAdminTitle = () => {
    if (referenceData?.type === "organization") {
      return "Organization Admin";
    } else if (referenceData?.type === "sub") {
      return "School Admin";
    } else if (referenceData?.type === "self") {
      return "School Admin";
    }
    return type === "school" ? "School Admin" : "Organization Admin";
  };

  // Get organization/school name from API or fallback
  const getInstitutionName = () => {
    if (loading) {
      return "Loading...";
    }
    if (referenceData?.name) {
      return referenceData.name;
    }
    return type === "school"
      ? "ABC International School, Delhi"
      : "ABC Organization, Delhi";
  };

  return (
    <div className="school-admin-settings-page">
      <div className="container-fluid p-0">
        <div className="p-0">
          {/* Header Section */}
          <div className="school-admin-header position-relative overflow-hidden">
            <div className="header-bg-image position-absolute end-0 top-0 h-100">
              <img
                src={Images.TopBarRightImg}
                alt="Header Background"
                className="h-100"
                style={{ opacity: 0.3 }}
              />
            </div>

            <div
              className="d-flex align-items-center mb-3 mb-md-4 container-fluid pt-3 pt-md-4 ps-3 ps-md-4 position-relative"
              style={{ zIndex: 1 }}
            >
              <div className="flex-shrink-0">
                <Avatar
                  name={getAdminTitle()}
                  src={referenceData?.image_url || undefined}
                  round="50px"
                  color="#E0E0E0"
                  size="60"
                  fgColor="#333"
                />
              </div>

              <div className="tchrdashboard-cmntitl mb-0 ms-3 text-white">
                <div className="fw-bold fs-4">{getAdminTitle()}</div>
                <div className="school_name opacity-75">
                  {getInstitutionName()}
                </div>
              </div>
            </div>

            <div
              className="container-fluid ps-3 ps-md-4 pe-3 pe-md-4 position-relative"
              style={{ zIndex: 1 }}
            >
              <ul className="nav nav-tabs settingsNavTabs" role="tablist">
                {filteredTabs.map((tab) => (
                  <li className="nav-item" role="presentation" key={tab.id}>
                    <NavLink
                      to={tab.path[type]}
                      className={({ isActive }) =>
                        `nav-link settingTab ${isActive ? "active" : ""}`
                      }
                      role="tab"
                    >
                      {tab.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Content Section */}
          <section className="p-2 p-sm-3 p-md-4">
            <div className="row">
              <div className="col-md-12">
                <div className="custom-card mt-3 mt-md-4">
                  <Outlet />
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default SettingsLayout;
